import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Stage, Layer, Text, Rect, Group } from 'react-konva'
import styles from './CustomerKonvaCanvas.module.scss'

/**
 * CustomerKonvaCanvas - Interactive template editor for customers
 *
 * Allows customers to click on text fields and edit them directly on the template.
 * Simplified version of the admin KonvaCanvas - no drag/resize, just text editing.
 */

// Calculate canvas dimensions with responsive scaling
function useCanvasDimensions(scalingFactor, templateWidth, templateHeight, maxWidth = 800) {
  const factor = scalingFactor || 1
  if (templateWidth && templateHeight) {
    const baseWidth = templateWidth / factor
    const baseHeight = templateHeight / factor

    if (baseWidth > maxWidth) {
      const scale = maxWidth / baseWidth
      return {
        width: maxWidth,
        height: baseHeight * scale,
        scale: scale
      }
    }
    return { width: baseWidth, height: baseHeight, scale: 1 }
  }
  return { width: 600, height: 400, scale: 1 }
}

// Calculate font size that fits text within width (auto-fit)
function calculateAutoFitFontSize(text, maxWidth, maxHeight, baseFontSize, minFontSize = 8, fontFamily = 'Arial', wrap = false, letterSpacing = 0) {
  if (!text || maxWidth <= 0) return baseFontSize

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  let fontSize = baseFontSize
  const safeWidth = maxWidth * 0.85
  const safeHeight = maxHeight * 0.85

  while (fontSize > minFontSize) {
    ctx.font = `${fontSize}px ${fontFamily}`

    if (wrap) {
      const words = text.split(' ')
      let line = ''
      let lineCount = 1
      const lineHeight = fontSize * 1.3

      for (let word of words) {
        const testLine = line + (line ? ' ' : '') + word
        const metrics = ctx.measureText(testLine)
        const totalWidth = metrics.width + (testLine.length * letterSpacing)
        if (totalWidth > safeWidth && line) {
          line = word
          lineCount++
        } else {
          line = testLine
        }
      }

      const totalHeight = lineCount * lineHeight
      if (totalHeight <= safeHeight) {
        return fontSize
      }
    } else {
      const metrics = ctx.measureText(text)
      const totalWidth = metrics.width + (text.length * letterSpacing)
      if (totalWidth <= safeWidth) {
        return fontSize
      }
    }

    fontSize--
  }

  return minFontSize
}

// Editable text field component
function EditableTextField({ field, displayText, scale, isSelected, isEditable, onSelect, onTextChange }) {
  const scaledLeft = (field.x || 0) * scale
  const scaledTop = (field.y || 0) * scale
  const scaledWidth = (field.width || 100) * scale
  const scaledHeight = (field.height || 30) * scale

  const baseFontSize = (field.text_size || field.textSize || 16) * scale
  const minFontSize = 8 * scale
  const fontFamily = field.fontName || field.font?.name || 'Arial'

  const effectiveFontSize = field.auto_fit || field.autoFit
    ? calculateAutoFitFontSize(
        displayText,
        scaledWidth,
        scaledHeight,
        baseFontSize,
        minFontSize,
        fontFamily,
        field.wrap,
        (field.spacing || 0) * scale
      )
    : baseFontSize

  return (
    <Group
      x={scaledLeft}
      y={scaledTop}
      width={scaledWidth}
      height={scaledHeight}
      onClick={isEditable ? onSelect : undefined}
      onTap={isEditable ? onSelect : undefined}
    >
      {/* Highlight box for editable fields */}
      {isEditable && (
        <Rect
          x={0}
          y={0}
          width={scaledWidth}
          height={scaledHeight}
          stroke={isSelected ? '#1097d2' : 'transparent'}
          strokeWidth={isSelected ? 2 : 0}
          fill={isSelected ? 'rgba(16, 151, 210, 0.1)' : 'transparent'}
          cornerRadius={2}
        />
      )}
      <Text
        x={0}
        y={0}
        width={scaledWidth}
        height={scaledHeight}
        text={displayText}
        fontSize={effectiveFontSize}
        fontFamily={fontFamily}
        fontStyle={field.fontStyle || 'normal'}
        textDecoration={field.textDecoration || ''}
        fill={field.color || '#000000'}
        letterSpacing={(field.spacing || 0) * scale}
        lineHeight={1.2}
        align={field.align || 'left'}
        verticalAlign="top"
        wrap={field.wrap ? 'word' : 'none'}
      />
    </Group>
  )
}

export default function CustomerKonvaCanvas({
  template,
  fields,
  imageUrl,
  maxWidth = 800,
  onFieldsChange
}) {
  const [selectedFieldId, setSelectedFieldId] = useState(null)
  const [editingFieldId, setEditingFieldId] = useState(null)
  const [editText, setEditText] = useState('')
  // Store edits by field ID to avoid collisions between fields with same column name
  const [fieldEdits, setFieldEdits] = useState({})
  const canvasWrapperRef = useRef()
  const textInputRef = useRef()

  const dimensions = useCanvasDimensions(
    template.scaling_factor,
    template.image_width,
    template.image_height,
    maxWidth
  )

  // Notify parent of field value changes (convert to fieldName-based for form submission)
  useEffect(() => {
    if (onFieldsChange) {
      const values = {}
      fields.forEach(field => {
        const fieldName = field.name || field.column_name || field.columnName
        if (fieldName && (field.customer_editable || field.customerEditable)) {
          // Only include customer-editable fields in form submission
          // Use edited value if exists, otherwise use original
          if (fieldEdits[field.id] !== undefined) {
            values[fieldName] = fieldEdits[field.id]
          } else {
            // Same priority as getDisplayText
            values[fieldName] = field.text || field.sample_text || field.sampleText || ''
          }
        }
      })
      onFieldsChange(values)
    }
  }, [fieldEdits, fields, onFieldsChange])

  // Get display text for a field - use field ID for lookups to avoid collisions
  const getDisplayText = (field) => {
    // Check if user has edited this specific field
    if (fieldEdits[field.id] !== undefined) {
      return fieldEdits[field.id]
    }
    // Fall back to field's original text
    // Priority: text (actual content) > sample_text (preview data)
    // Use explicit null checks since empty string should be falsy but we want to show it
    if (field.text != null && field.text !== '') {
      return field.text
    }
    if (field.sample_text != null && field.sample_text !== '') {
      return field.sample_text
    }
    if (field.sampleText != null && field.sampleText !== '') {
      return field.sampleText
    }
    return ''
  }

  // Check if field is customer-editable
  const isFieldEditable = (field) => {
    return field.customer_editable || field.customerEditable
  }

  // Handle field selection
  const handleFieldSelect = useCallback((field) => {
    const fieldId = field.id
    setSelectedFieldId(fieldId)

    // Start editing immediately on click for editable fields
    setEditingFieldId(fieldId)
    // Use the current display text (edited or original) - same priority as getDisplayText
    let initialText = ''
    if (fieldEdits[fieldId] !== undefined) {
      initialText = fieldEdits[fieldId]
    } else if (field.text != null && field.text !== '') {
      initialText = field.text
    } else if (field.sample_text != null && field.sample_text !== '') {
      initialText = field.sample_text
    } else if (field.sampleText != null && field.sampleText !== '') {
      initialText = field.sampleText
    }
    setEditText(initialText)

    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus()
        textInputRef.current.select()
      }
    }, 50)
  }, [fieldEdits])

  // Handle clicking on empty area
  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage()) {
      setSelectedFieldId(null)
      if (editingFieldId) {
        // Save any pending edit
        handleSaveEdit()
      }
    }
  }, [editingFieldId])

  // Save the current edit
  const handleSaveEdit = useCallback(() => {
    if (!editingFieldId) return

    // Store edit by field ID to avoid collisions
    setFieldEdits(prev => ({
      ...prev,
      [editingFieldId]: editText
    }))

    setEditingFieldId(null)
    setEditText('')
  }, [editingFieldId, editText])

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      setEditingFieldId(null)
      setEditText('')
    } else if (e.key === 'Tab') {
      e.preventDefault()
      handleSaveEdit()

      // Move to next editable field
      const editableFields = fields.filter(isFieldEditable)
      const currentIndex = editableFields.findIndex(f => f.id === editingFieldId)
      const nextIndex = (currentIndex + 1) % editableFields.length
      const nextField = editableFields[nextIndex]
      if (nextField) {
        setTimeout(() => handleFieldSelect(nextField), 50)
      }
    }
  }, [handleSaveEdit, fields, editingFieldId, handleFieldSelect])

  // Get the currently editing field
  const editingField = editingFieldId ? fields.find(f => f.id === editingFieldId) : null

  return (
    <div className={styles.canvasContainer}>
      <div
        ref={canvasWrapperRef}
        className={styles.canvasWrapper}
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        {/* Background template image */}
        <img
          src={imageUrl}
          alt="Template background"
          className={styles.backgroundImage}
          style={{ width: dimensions.width, height: dimensions.height }}
        />

        {/* Konva canvas for text fields */}
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          onClick={handleStageClick}
          onTap={handleStageClick}
          className={styles.konvaStage}
        >
          <Layer>
            {fields.map((field) => {
              const isEditable = isFieldEditable(field)
              const displayText = getDisplayText(field)

              return (
                <EditableTextField
                  key={field.id}
                  field={field}
                  displayText={displayText}
                  scale={dimensions.scale}
                  isSelected={selectedFieldId === field.id}
                  isEditable={isEditable}
                  onSelect={() => handleFieldSelect(field)}
                />
              )
            })}
          </Layer>
        </Stage>
      </div>

      {/* Text editing input - positioned near the selected field */}
      {editingField && (
        <div className={styles.editInputContainer}>
          <div className={styles.editInputWrapper}>
            <label className={styles.editLabel}>
              {editingField.field_label || editingField.fieldLabel || editingField.name || editingField.column_name || 'Edit Text'}:
            </label>
            <textarea
              ref={textInputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSaveEdit}
              className={styles.editInput}
              rows={2}
              placeholder="Enter your text here..."
            />
            <div className={styles.editHint}>
              Press Enter to save, Tab for next field, Esc to cancel
            </div>
          </div>
        </div>
      )}

      <div className={styles.instructions}>
        <p>Click on any highlighted text field to edit it. Your changes appear instantly on the template.</p>
      </div>
    </div>
  )
}
